<!-- 
  Application root layout

  This layout is used to wrap the entire application. It includes the header, footer,
  and the main content area. Addon components like the cookies banner, service announcement,
  and theme activation are also included in this layout.

  lazy-loading is used for the footer and cookies banner components to improve the initial
  load time of the application.

  The layout is also responsible for the following features:
  - Setting the theme based on the business rules
  - Setting the locale
  - Checking if the user is authenticated
 -->
<template>
  <div class="h-100">
    <!--This div is used to announce the title changes during CSR. Refer: page-title.ts for more details -->
    <div id="page-title" aria-live="polite" class="sr-only"></div>
    <div v-if="skipRenderingPage" class="h-100">
      <CommonLoader />
    </div>
    <div
      v-if="!skipRenderingPage"
      ref="dashboardContainerDiv"
      class="dashboard-container h-100"
      :class="{
        cookiesBannerVisible: isCookieBannerVisible
      }"
    >
      <CommonLoader />
      <ClientOnly>
        <LazyCommonCookiesBanner v-show="getCookiesBannerState()" />
      </ClientOnly>

      <div
        ref="mainAppDiv"
        class="wrapper bg-white"
        :class="{
          'bg-gray': isGrayBackground,
          'show-cookies-banner': isCookieBannerVisible,
          'd-flex flex-column': isParentFullScreenPage
        }"
      >
        <CommonSkipToMainContent v-if="showSkipToMainContent" />
        <CommonHeader />
        <LazyCommonServiceAnnouncement />
        <ClientOnly>
          <LazyTeacherDashboardFeatureInfoBanner
            v-if="showTeacherInformationBanner"
            :has-service-announcements="hasServiceAnnouncements"
            @close-info-banner="showTeacherInformationBanner = false"
          />
        </ClientOnly>
        <div
          :class="{
            'container-fluid px-0': !isParentFullScreenPage,
            'd-flex flex-column parent-db-container': isParentFullScreenPage,
            informationBannerVisible:
              showTeacherInformationBanner && !hasServiceAnnouncements
          }"
        >
          <!-- Banner for downloading the Desktop App-->
          <ClientOnly>
            <LazyCommonDesktopAppBanner v-if="showDesktopBanner" />
          </ClientOnly>
          <div
            :class="{
              'page-wrapper': !isParentFullScreenPage,
              'h-100 d-flex': isParentFullScreenPage
            }"
          >
            <NuxtPage />
          </div>
        </div>
      </div>
      <!-- NPS Survey, only shown on Dashboard page -->
      <LazyCommonNPSSurvey
        v-if="
          [
            '/teacher/dashboard',
            '/teacher/library',
            '/teacher/training',
            '/learner/dashboard'
          ].includes($route.path)
        "
      />
      <div
        v-if="!$route.path.includes('/activateMaterial/')"
        :class="{
          disabled: useLearnerProductsStore()?.isActivationNeeded
        }"
      >
        <LazyCommonFooter />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
onBeforeMount(() => {
  // If the user is not authenticated, skip rendering the page and show the loader
  if (skipRenderingPage.value) {
    showOverlayLoader();
  }
});
/**
 * Initialize the APP in both CSR and SSR modes.
 *
 * Note: The same code is also being used in the following pages:
 * - Class Invitation Accept page.
 *  - Path: /pages/invitation/[schoolId]/class_enrollment/[classId]/[invitationId]/accept
 * - Class Invitation Success page.
 *  - Path: /pages/invitation/success
 * - Error page
 *  - Path: /error
 */

/**
 * Handling for the SSR Mode:
 *
 * Load the user data from the session on the server into the Authenticate Store.
 *
 * Also, activate the user's theme is activated by injecting the theme variables into
 * the head of the page.
 *
 * Also, store the above user data and theme into the Nuxt Async Data, so that it can
 * be retrieved in the CSR mode.
 */
if (import.meta.server) {
  const event = useRequestEvent();
  const userSession = event?.context.session;

  if (userSession) {
    const userData = {
      email: userSession.email,
      userId: userSession.extUserId,
      role: userSession.role,
      firstName: userSession.firstName,
      lastName: userSession.lastName,
      isLtiSession: userSession.launchType === 'lti',
      integrationName: userSession.integrationName || ''
    };

    // Load user data into the Authenticate Store
    useAuthenticateStore().load(userData);

    // Activate theme on SSR from session
    const themeVariables = useThemeService().activateTheme(userSession.theme);

    // Set the theme in the head
    useHead({
      style: [
        {
          innerHTML: themeVariables,
          id: 'theme-variables'
        }
      ]
    });

    // Store the user data and theme in the Nuxt Async Data
    await Promise.all([
      useAsyncData('authenticate-data', () => Promise.resolve(userData)),
      useAsyncData('theme', () => Promise.resolve(userSession.theme))
    ]);
  }
}

/**
 * Handling for the CSR Mode:
 *
 * Retrieve the user data from the Nuxt Async data and load it into the Authenticate Store.
 * Also, retrieve the theme from the Nuxt Async data and activate the theme.
 */
if (import.meta.client) {
  // Extract the user data from the authenticate-data
  const { data } = useNuxtData('authenticate-data');

  if (data.value) {
    // Load it into the Authenticate Store
    useAuthenticateStore().load(data.value);

    // Extract the theme from the theme data
    const { data: theme } = useNuxtData('theme');

    // Activate theme
    const themeVariables = useThemeService().activateTheme(theme.value);

    // Set the theme in the head
    useHead({
      style: [
        {
          innerHTML: themeVariables,
          id: 'theme-variables'
        }
      ]
    });
  }
}
const nuxtApp = useNuxtApp();
/**
 * On mounted, activate the theme and set the locale
 *
 * Theme service is used to configure and activate
 * the appropriate theme (Adult, Primary, Secondary)
 * based on CUP&A business rules.
 *
 * Locale service is used to set the locale
 */
onMounted(async () => {
  const { setLocale } = useI18n();
  const { lang, informationBanner } = useLocalStorage();
  const config = useRuntimeConfig();
  const route = useRoute();
  let locale = window.localStorage.getItem(lang);
  const supportedLangs = config.public.supportedLangs;
  const userData = useAuthenticateStore(); /**
   * Listens for the 'user:desktop-app-banner-visible' event and updates the value of the 'showDesktopBanner' variable accordingly.
   * @param {boolean} bannerVisible - A boolean value indicating whether the desktop app banner is visible or not.
   */
  //Banner has been moved from the learner/index.vue component to layout because it needs to be spread across the entire page
  useNuxtApp().$listen('user:desktop-app-banner-visible', (bannerVisible) => {
    showDesktopBanner.value = bannerVisible;
  });
  nuxtApp.$isAppMounted.value = true;
  if (locale) {
    locale = locale.toLowerCase();
    locale = locale.charAt(0).toUpperCase() + locale.slice(1);
    if (
      !supportedLangs.includes(locale) ||
      ['teacher', 'admin', 'superadmin'].indexOf(userData?.role) > -1
    ) {
      locale = 'En';
    }
    await setLocale(locale);
  }

  /**
   * Check if the information banner is enabled via the config.
   *
   * If the information banner is enabled,
   * read the information banner from the local storage
   * and set the value of the 'showTeacherInformationBanner' variable accordingly.
   *
   * It is set true only for three teacher routes.
   */
  if (config.public.isFeatureInfomationBannerEnable) {
    //read information banner from local storage
    const isBannerVisible = window.localStorage.getItem(informationBanner);
    if (
      isBannerVisible !== 'false' &&
      ['/teacher/dashboard', '/teacher/library', '/teacher/training'].includes(
        route.path
      )
    ) {
      showTeacherInformationBanner.value = true;
      // check for service announcements
      await checkServiceAnnouncements();
    }
  }
});

/**
 * Cookie banner service is used to manage the cookies banner
 * and calculate the current state of the cookies banner
 */
const { calcCookiesBannerState, getCookiesBannerState } = useCookieBanner();
calcCookiesBannerState();

/**
 * showDesktopBanner is a reactive variable that is used to
 * determine whether the desktop app banner should be displayed or not.
 * It is set to true when the 'user:desktop-app-banner-visible' event is emitted.
 */
const showDesktopBanner = shallowRef(false);

/**
 * Adding watchEffect since dynamic classes do not work for SSR
 * It is recommended to keep CSR and SSR view similar
 */

const mainAppDiv = shallowRef(null);
const dashboardContainerDiv = shallowRef(null);
const isCookieBannerVisible = shallowRef(false);

/**
 * showTeacherInformationBanner is used to add margin-top to the page content,
 *  when the information banner is visible.
 */
const showTeacherInformationBanner = shallowRef(false);
/**
 * This param checks for the presence of service announcements
 */
const hasServiceAnnouncements = shallowRef();
/**
 * skipRenderingPage is used to skip rendering the page on SSR if the user is not authenticated
 * User session Validation Plugin on client side will handle the redirection to the login page/relogin scenarios
 */
const skipRenderingPage = shallowRef(!useAuthenticateStore().userId);

watchEffect(() => {
  if (dashboardContainerDiv.value) {
    isCookieBannerVisible.value = getCookiesBannerState();
  }
});

const isParentFullScreenPage = computed(() => {
  return ['/parent/dashboard', '/parent/createChild'].includes(useRoute().path);
});

/**
 * Computed property isGrayBackground is used for gray background based on condition
 *  used for create-class route /teacher/create-class
 */
const isGrayBackground = computed(() => {
  return ['/teacher/create-class'].includes(useRoute().path);
});

/**
 * Computed property to show CommonSkipToMainContent only on specific routes
 */
const showSkipToMainContent = computed(() => {
  const allowedRoutes = [
    '/my-profile',
    '/desktop-installers/downloads',
    '/learner/dashboard',
    '/learner/startlearning/addClass',
    '/learner/startlearning/main',
    '/learner/startlearning/addMaterial',
    '/teacher/dashboard',
    '/teacher/library',
    '/teacher/training',
    '/teacher/create-class',
    '/teacher/startteaching/joinSchool'
  ];

  // Check for dynamic route: /teacher/[school]/bundle/[code]/view
  const isDynamicBundleRoute =
    useRoute().name === 'teacher-school-bundle-code-view';

  return allowedRoutes.includes(useRoute().path) || isDynamicBundleRoute;
});

/**
 * desktopAppBannerHandler is a method that is called when the 'user:desktop-app-banner-visible' event is emitted.
 * It updates the value of the 'showDesktopBanner' variable.
 * @param {boolean} bannerVisible - A boolean value indicating whether the desktop app banner is visible or not.
 */
const desktopAppBannerHandler = (bannerVisible: boolean) => {
  showDesktopBanner.value = bannerVisible;
};

/**
 * The method will check for the existence of announcements
 */
async function checkServiceAnnouncements() {
  await useAnnouncement()
    .getAnnouncements()
    .then((res: any) => {
      if (res && res.results.length > 0) {
        hasServiceAnnouncements.value = true;
      } else {
        hasServiceAnnouncements.value = false;
      }
    });
}

onBeforeUnmount(() => {
  //  remove the desktopapp banner visisbility event listener
  useNuxtApp().$removeListen(
    'user:desktop-app-banner-visible',
    desktopAppBannerHandler
  );
});
</script>

<style scoped lang="scss">
@import '@/assets/scss/helper.scss';

.dashboard-container {
  display: flex;
  height: 100%;
  padding: inherit;
  flex-direction: column;

  //cookies banner styles
  .show-cookies-banner {
    margin-top: 40px;

    @media (max-width: 576px) {
      margin-top: 72px;
    }
  }

  .wrapper {
    flex: 1 0 auto;
    .page-wrapper {
      padding: 0 16px;

      @include media-breakpoint-up(md) {
        padding: 0 24px;
      }

      @include media-breakpoint-up(xl) {
        padding: 0 32px;
      }
    }

    &.bg-gray {
      background-color: var(--cup-general-color-1);

      @include media-breakpoint-up(md) {
        /* To remove important later */
        background-color: #f8f8f8 !important;
      }
    }
    .parent-db-container {
      flex: 1 0 auto;
    }
  }

  .disabled {
    pointer-events: none;
    opacity: 0.2;
  }

  a {
    cursor: pointer;
  }

  a:hover {
    text-decoration: underline;
  }

  .informationBannerVisible {
    padding-top: 176px;
    @include media-breakpoint-up(sm) {
      padding-top: 128px;
    }
    @include media-breakpoint-up(md) {
      padding-top: 82px;
    }
    @include media-breakpoint-up(lg) {
      padding-top: 66px;
    }
  }
}
</style>
